/**
 * Email subcategories per Concierge inbox tab, as the classifier emits them on
 * `emailSubCategory`. One list drives the filter chips above each tab's
 * listing, the per-row subcategory chip and the Reprocess dialog's options.
 *
 * The values mirror VALID_SUB_CATEGORIES in the extractor's
 * email_classifier/config.py. Membership there is an exact set match, so a
 * value outside it resolves no schema and a reprocess into it extracts
 * nothing. Keyed by tab value, which is also how the Reprocess dialog spells
 * its categories.
 */

export interface SubCategoryOption {
  readonly value: string;
  readonly label: string;
  /**
   * Still on stored mail, so it can be filtered on, but no longer in the
   * extractor's set, so it is not offered for reprocessing.
   */
  readonly legacy?: boolean;
}

const UNKNOWN: SubCategoryOption = { value: 'unknown', label: 'Unknown' };
const OTHER: SubCategoryOption = { value: 'other', label: 'Other' };
const REQUEST: SubCategoryOption = { value: 'request', label: 'Request' };
const RESPONSE: SubCategoryOption = { value: 'response', label: 'Response' };
const SIGNED_APP: SubCategoryOption = {
  value: 'signedApp',
  label: 'Signed App',
};
const ANSWERS: SubCategoryOption = { value: 'answers', label: 'Answers' };
const INQUIRY: SubCategoryOption = { value: 'inquiry', label: 'Inquiry' };

const PRODUCTS: ReadonlyArray<SubCategoryOption> = [
  { value: 'Cyber', label: 'Cyber' },
  { value: 'Tech E&O', label: 'Tech E&O' },
  { value: 'Management Liability', label: 'Management Liability' },
];

export const SUB_CATEGORIES_BY_TAB: Readonly<
  Record<string, ReadonlyArray<SubCategoryOption>>
> = {
  Submission: [...PRODUCTS, UNKNOWN],
  LossRuns: [REQUEST, RESPONSE],
  RiskEng: [
    { value: 'contactform', label: 'Contact Form' },
    { value: 'darkwebreport', label: 'Dark Web Report' },
    { value: 'riskreport', label: 'Risk Report' },
    { value: 'mpt', label: 'MPT' },
    { value: 'subjectivitycall', label: 'Subjectivity Call' },
    { value: 'riskassessment', label: 'Risk Assessment' },
    { value: 'mdr', label: 'MDR' },
    { value: 'riskdatarequest', label: 'Risk Data Request' },
    { value: 'generic', label: 'General' },
  ],
  Claims: [
    { value: 'fnol', label: 'FNOL' },
    // The adjuster's acknowledgement letter, now fnol/adjuster_ack.
    { value: 'claimreadyselfcc', label: 'Adjuster Ack', legacy: true },
  ],
  // The workstream only. CLAIMS_OPS also has a subSubCategory (the act), and
  // extraction is gated on that, not on this.
  ClaimsOps: [
    { value: 'panel_engagement', label: 'Panel Engagement' },
    { value: 'matter_intake', label: 'Matter Intake' },
    { value: 'guidance', label: 'Guidance' },
    { value: 'claim_inquiry', label: 'Claim Inquiry' },
    { value: 'bulletin', label: 'Bulletin' },
    { value: 'system', label: 'System' },
    { value: 'financial', label: 'Financial' },
    OTHER,
  ],
  Binders: [REQUEST, SIGNED_APP],
  CRS: [
    { value: 'gmi', label: 'GMI' },
    { value: 'spearTip', label: 'SpearTip' },
    { value: 'accorian', label: 'Accorian' },
    UNKNOWN,
  ],
  Subjectivity: [{ value: 'nkll', label: 'NKLL' }, SIGNED_APP, ANSWERS, OTHER],
  Renewal: [...PRODUCTS, ANSWERS, INQUIRY, OTHER, UNKNOWN],
  Cancellation: [{ value: 'noc', label: 'Notice of Cancellation' }],
  // Console-side: the extractor does not classify Compliance.
  Compliance: [
    { value: 'Review', label: 'Review' },
    { value: 'Audit', label: 'Audit' },
    { value: 'Other', label: 'Other' },
  ],
  General: [INQUIRY],
  Finance: [
    { value: 'remittance', label: 'Remittance' },
    { value: 'bank_txn', label: 'Bank Transaction' },
    UNKNOWN,
  ],
};

/** A tab's subcategories, or none for a tab the classifier does not split. */
export const subCategoriesForTab = (
  tab: string
): ReadonlyArray<SubCategoryOption> => SUB_CATEGORIES_BY_TAB[tab] ?? [];

/** The values the Reprocess dialog offers: everything but legacy values. */
export const reprocessSubCategories = (tab: string): string[] =>
  subCategoriesForTab(tab)
    .filter((subCategory) => !subCategory.legacy)
    .map((subCategory) => subCategory.value);

/**
 * Row chip label for a message's subcategory on a tab, or null when the
 * message has none or one the tab does not know (no chip is rendered then).
 */
export const getSubCategoryLabel = (
  tab: string,
  emailSubCategory?: string | null
): string | null => {
  if (!emailSubCategory) {
    return null;
  }
  const stored = emailSubCategory.toLowerCase();
  const match = subCategoriesForTab(tab).find(
    (subCategory) => subCategory.value.toLowerCase() === stored
  );
  return match ? match.label : null;
};

/**
 * Message-list filter for a category tab: the category clause, narrowed to one
 * subcategory when a filter chip is selected.
 */
export const buildCategoryTabFilter = (
  apiCategory: string,
  subCategory: string | null
): Record<string, { eq: string }> => {
  const filter: Record<string, { eq: string }> = {
    'msgCategory.keyword': { eq: apiCategory },
  };
  if (subCategory) {
    filter['emailSubCategory.keyword'] = { eq: subCategory };
  }
  return filter;
};
